/**
 * Domain hook for Defects module data management
 * Centralizes all defects-related storage and business logic
 * 
 * @example
 * const { defects, saveDefect, updateDefectStatus, getCriticalDefects } = useDefectsData();
 */

import { useStorageState } from '../useStorageState';
import { useVessel } from '../../contexts/VesselContext';
import { usePermissions } from '../usePermissions';
import { createAuditEntry, appendToAuditLog } from '../../utils/auditUtils';

export const useDefectsData = () => {
  const { currentVesselId } = useVessel();
  const { currentUser } = usePermissions();
  const userName = currentUser?.name || 'Unknown User';

  // Base key only - vessel scoping handled by useStorageState
  const { value: defects = [], setValue: setDefects } = useStorageState('defects', []);
  
  // Save a new defect
  const saveDefect = async (defectData) => {
    const now = new Date().toISOString();

    const newDefect = {
      ...defectData,
      id: defectData.id || `DEF-${Date.now()}`,
      reportedDate: defectData.reportedDate || now,
      status: defectData.status || 'open',
      vesselId: currentVesselId,

      // v4.2 Audit Trail (camelCase) - FT-06b: Object format for audit trail
      createdBy: {
        userId: currentUser?.id,
        name: currentUser?.name,
        role: currentUser?.role
      },
      createdAt: now,
      updatedBy: null,
      updatedAt: null
    };

    const updated = [...defects, newDefect];
    return await setDefects(updated);
  };
  
  // Update defect status - FT-06c: Added audit logging for status changes
  const updateDefectStatus = async (defectId, newStatus, resolution = null) => {
    const existingDefect = defects.find(d => d.id === defectId);
    const now = new Date().toISOString();

    // Create audit entry for status change
    const auditEntry = createAuditEntry(
      existingDefect,
      { ...existingDefect, status: newStatus, ...(resolution && { resolution }) },
      currentUser,
      { action: 'status_change', fieldsToTrack: ['status', 'resolution'] }
    );

    const updated = defects.map(d => {
      if (d.id === defectId) {
        return {
          ...d,
          status: newStatus,
          resolution,
          resolvedDate: newStatus === 'resolved' ? now : d.resolvedDate,
          lastModified: now,
          updatedBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role },
          updatedAt: now,
          auditLog: appendToAuditLog(d.auditLog, auditEntry)
        };
      }
      return d;
    });

    return await setDefects(updated);
  };
  
  // Update defect details - FT-06c: Added audit logging and object format for updatedBy
  const updateDefect = async (defectId, updates) => {
    const now = new Date().toISOString();
    const existingDefect = defects.find(d => d.id === defectId);

    // Create audit entry for the edit
    const auditEntry = createAuditEntry(existingDefect, { ...existingDefect, ...updates }, currentUser);

    const updated = defects.map(d => {
      if (d.id === defectId) {
        return {
          ...d,
          ...updates,
          lastModified: now,  // Keep for backward compatibility

          // v4.2 Audit Trail (camelCase) - FT-06b/c: Object format with audit log
          updatedBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role },
          updatedAt: now,
          auditLog: appendToAuditLog(d.auditLog, auditEntry)
        };
      }
      return d;
    });

    return await setDefects(updated);
  };
  
  // Delete a defect
  // FT-458: soft-delete via tombstone — stamp deleted_at + KEEP in array so the
  // deletion propagates through the cloud∪local sync union (a hard filter lets
  // the cloud copy resurrect it). Mirrors the converted useDrillsData.deleteDrill
  // + useIncidentsData.remove siblings. (This hook's deleteDefect isn't wired to
  // any live UI today, but is fixed defensively so a future wiring can't
  // reintroduce the resurrection bug.)
  const deleteDefect = async (defectId) => {
    const nowIso = new Date().toISOString();
    const updated = defects.map(d =>
      d.id === defectId
        ? { ...d, deleted_at: nowIso, deleted_by: userName, updatedAt: Date.now() }
        : d
    );
    return await setDefects(updated);
  };

  // FT-06d: Resolve a defect (any user can resolve - marks as "Resolved" awaiting skipper closure)
  const resolveDefect = async (defectId, resolutionData = {}) => {
    const existingDefect = defects.find(d => d.id === defectId);
    if (!existingDefect) throw new Error('Defect not found');

    const now = new Date().toISOString();

    // Create audit entry for resolution
    const auditEntry = createAuditEntry(
      existingDefect,
      { ...existingDefect, status: 'Resolved' },
      currentUser,
      { action: 'resolved', fieldsToTrack: ['status'] }
    );

    const updated = defects.map(d => {
      if (d.id === defectId) {
        return {
          ...d,
          ...resolutionData,
          status: 'Resolved',
          resolvedDate: now,
          lastModified: now,
          // FT-06d: Flat two-field pattern for resolution attribution
          resolvedBy: currentUser?.id,
          resolvedByName: currentUser?.name,
          resolvedAt: now,
          // Standard audit trail
          updatedBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role },
          updatedAt: now,
          auditLog: appendToAuditLog(d.auditLog, auditEntry)
        };
      }
      return d;
    });

    return await setDefects(updated);
  };

  // FT-06d: Close a defect (skipper/fleet/super_admin only - locks the record)
  const closeDefect = async (defectId, closeoutData = {}) => {
    const existingDefect = defects.find(d => d.id === defectId);
    if (!existingDefect) throw new Error('Defect not found');

    const now = new Date().toISOString();

    // Create audit entry for closure
    const auditEntry = createAuditEntry(
      existingDefect,
      { ...existingDefect, status: 'Closed' },
      currentUser,
      { action: 'closed', fieldsToTrack: ['status'] }
    );

    const updated = defects.map(d => {
      if (d.id === defectId) {
        return {
          ...d,
          ...closeoutData,
          status: 'Closed',
          date_closed: now,
          lastModified: now,
          // FT-06d: Flat two-field pattern for closure attribution (regulatory compliance)
          // FT-06e: Include closedByRole for "Name (Role)" display
          closedBy: currentUser?.id,
          closedByName: currentUser?.name,
          closedByRole: currentUser?.role || '',
          closedAt: now,
          // Standard audit trail
          updatedBy: { userId: currentUser?.id, name: currentUser?.name, role: currentUser?.role },
          updatedAt: now,
          auditLog: appendToAuditLog(d.auditLog, auditEntry)
        };
      }
      return d;
    });

    return await setDefects(updated);
  };
  
  // Get defects by status
  const getDefectsByStatus = (status) => {
    return defects.filter(d => d.status === status);
  };
  
  // Get critical/safety-critical defects
  const getCriticalDefects = () => {
    return defects.filter(d => 
      d.priority === 'critical' || 
      d.priority === 'high' ||
      d.safetyCritical === true
    );
  };
  
  // Get open defects count
  const getOpenDefectsCount = () => {
    return defects.filter(d => d.status === 'open' || d.status === 'in_progress').length;
  };
  
  // Get defects requiring immediate attention
  const getUrgentDefects = () => {
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);
    
    return defects.filter(d => {
      if (d.status === 'resolved') return false;
      
      // Critical defects are always urgent
      if (d.priority === 'critical' || d.safetyCritical) return true;
      
      // High priority defects open > 7 days
      if (d.priority === 'high') {
        const reportedDate = new Date(d.reportedDate);
        const daysSinceReported = Math.floor((new Date() - reportedDate) / (1000 * 60 * 60 * 24));
        return daysSinceReported > 7;
      }
      
      // Any defect open > 30 days
      const reportedDate = new Date(d.reportedDate);
      return reportedDate < thirtyDaysAgo;
    });
  };
  
  // Calculate defect resolution rate
  const getResolutionRate = () => {
    if (defects.length === 0) return 100;
    
    const resolved = defects.filter(d => d.status === 'resolved').length;
    return Math.round((resolved / defects.length) * 100);
  };
  
  return {
    // Data
    defects,

    // Actions
    saveDefect,
    updateDefectStatus,
    updateDefect,
    deleteDefect,
    setDefects,
    resolveDefect,   // FT-06d: Any user can resolve
    closeDefect,     // FT-06d: Skipper+ only can close (locks record)

    // Queries
    getDefectsByStatus,
    getCriticalDefects,
    getOpenDefectsCount,
    getUrgentDefects,
    getResolutionRate
  };
};